import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Portal } from '../Portal/Portal';
import { positions, themes, TooltipContentPopupClassName } from './Tooltip';
import { flipHorizontal, flipVertical, getXPos, getYPos } from './helpers';
import './FixedTooltip.scss';

/*
 * A JavaScript-powered tooltip that uses fixed positioning, for use when the standard tooltip doesn't cut it.
 */
export const FixedTooltip = ({
    boundaryRef,
    children,
    className,
    content: Content,
    gapSize = 5,
    hasBorder,
    hasSetWidth,
    isContentCentered,
    isEnabled = true,
    isExpanded,
    popupClassName,
    position = 'top',
    shouldOpenOnFocus = false,
    shouldUseDisabledOverlay,
    theme = 'default',
}) => {
    const [tooltipState, setTooltipState] = useState({ position, xPos: null, yPos: null });
    const triggerRef = useRef();
    const tooltipRef = useRef();

    const handleMouseEnter = () => {
        const trigger = triggerRef.current;
        const tooltip = tooltipRef.current;
        if (!trigger || !tooltip) return;
        const triggerRect = trigger.getBoundingClientRect();

        const measurements = {
            position,
            triggerWidth: trigger.offsetWidth,
            triggerHeight: trigger.offsetHeight,
            triggerXPos: triggerRect.x,
            triggerYPos: triggerRect.y,
            tooltipWidth: tooltip.offsetWidth,
            tooltipHeight: tooltip.offsetHeight,
        };

        let xPos = getXPos(measurements);
        let yPos = getYPos(measurements);

        const boundary = boundaryRef?.current;
        const boundaryRect = boundary?.getBoundingClientRect();
        const hasBoundary = boundary && boundaryRect;

        const yMin = hasBoundary ? boundaryRect.y : 0;
        const yMax = hasBoundary ? boundaryRect.y + boundary.offsetHeight : window.innerHeight;
        const xMin = hasBoundary ? boundaryRect.x : 0;
        const xMax = hasBoundary ? boundaryRect.x + boundary.offsetWidth : window.innerWidth;

        const isOutOfBoundsY = yPos < yMin || yPos + measurements.tooltipHeight > yMax;
        const isOutOfBoundsX = xPos < xMin || xPos + measurements.tooltipWidth > xMax;

        let flippedPosition = position;
        if (isOutOfBoundsY) flippedPosition = flipVertical(flippedPosition);
        if (isOutOfBoundsX) flippedPosition = flipHorizontal(flippedPosition);

        if (flippedPosition !== position) {
            xPos = getXPos({ ...measurements, position: flippedPosition });
            yPos = getYPos({ ...measurements, position: flippedPosition });
        }

        setTooltipState({ position: flippedPosition, xPos, yPos });
    };

    const handleMouseLeave = () => {
        setTooltipState((prevState) => ({ ...prevState, xPos: null, yPos: null }));
    };

    const isVisible = tooltipState.xPos !== null && tooltipState.yPos !== null;
    const currentPosition = tooltipState.position;

    const tooltipStyle = {
        left: tooltipState.xPos || 0,
        paddingRight: ['left', 'left-top', 'left-bottom'].includes(currentPosition) ? gapSize : 0,
        paddingLeft: ['right', 'right-top', 'right-bottom'].includes(currentPosition) ? gapSize : 0,
        paddingTop: ['bottom', 'bottom-left', 'bottom-right'].includes(currentPosition)
            ? gapSize
            : 0,
        paddingBottom: ['top', 'top-left', 'top-right'].includes(currentPosition) ? gapSize : 0,
        position: 'fixed',
        top: tooltipState.yPos || 0,
        visibility: isVisible ? 'visible' : 'hidden',
    };

    const containerStyles = classNames(className, 'cc-fixed-tooltip', {
        'cc-fixed-tooltip--expanded': isExpanded,
    });

    const popupStyles = classNames(
        popupClassName,
        TooltipContentPopupClassName,
        { [`${TooltipContentPopupClassName}--visible`]: isVisible },
        { [`${TooltipContentPopupClassName}--bordered`]: hasBorder },
        { [`${TooltipContentPopupClassName}--set-width`]: hasSetWidth },
        { [`${TooltipContentPopupClassName}--set-width-wide`]: hasSetWidth === 'wide' },
        { [`${TooltipContentPopupClassName}--centered`]: isContentCentered },
        `${TooltipContentPopupClassName}--theme-${theme}`,
    );

    if (!isEnabled) return children;

    return (
        <>
            <span
                className={containerStyles}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                onFocus={shouldOpenOnFocus ? handleMouseEnter : undefined}
                onBlur={shouldOpenOnFocus ? handleMouseLeave : undefined}
                onKeyDown={
                    shouldOpenOnFocus
                        ? (event) => {
                              if (event.key === 'Escape') handleMouseLeave();
                          }
                        : undefined
                }
                ref={triggerRef}
            >
                {shouldUseDisabledOverlay && (
                    <div
                        className="cc-fixed-tooltip__disabled-overlay"
                        onMouseLeave={handleMouseLeave}
                    />
                )}
                {children}
            </span>
            <Portal portalId="portal-root-tooltips" shouldEmptyHtml={false}>
                <span
                    className={containerStyles}
                    style={tooltipStyle}
                    ref={tooltipRef}
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleMouseLeave}
                >
                    <span className={popupStyles}>
                        <Content />
                    </span>
                </span>
            </Portal>
        </>
    );
};

FixedTooltip.propTypes = {
    bondaryRef: PropTypes.object,
    content: PropTypes.func,
    className: PropTypes.string,
    gapSize: PropTypes.number,
    hasBorder: PropTypes.bool,
    hasSetWidth: PropTypes.oneOfType([PropTypes.bool, PropTypes.oneOf(['wide'])]),
    isContentCentered: PropTypes.bool,
    isEnabled: PropTypes.bool,
    isExpanded: PropTypes.bool,
    shouldOpenOnFocus: PropTypes.bool,
    position: PropTypes.oneOf(positions),
    /*
     * @Prop: shouldUseDisabledOverlay is useful when the triggering component is disabled,
     * which may not fire off the mouseLeave event.
     */
    shouldUseDisabledOverlay: PropTypes.bool,
    theme: PropTypes.oneOf(themes),
};
